import { z } from 'zod';

import { localeSchema, type Locale } from './locales.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DEĞİŞİKLİKLER — /changelog sayfasının sözleşmesi
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürünün sürüm günlüğü: ne zaman ne değişti. Kayıtlar `changelog_entries` +
 * `changelog_entry_translations` tablolarında; bu dosya arayüzün gördüğü
 * BİÇİMİ tanımlıyor.
 *
 * ---------------------------------------------------------------------------
 * Blogun ve yasal metnin akrabası — üç yerde ayrılıyor
 * ---------------------------------------------------------------------------
 * | | Blog | Yasal | Değişiklikler |
 * |---|---|---|---|
 * | Gövde | serbest blok listesi | serbest blok listesi + tablo | SABİT: etiketli satırlar |
 * | Detay sayfası | var (`/blog/<slug>`) | var (`/privacy`) | YOK — tek sayfa, çıpalı |
 * | Eksik çeviri (`en`/`tr`) | kayıt o dilde görünmez | başka dile düşer | kayıt o dilde görünmez |
 * | Eksik çeviri (diğer 11 dil) | en → tr'ye düşer | en → tr → eldeki ilk dile düşer | en → tr'ye düşer |
 *
 * Birinci satır bu dosyanın en önemli kararı ve gerekçesi
 * `changelogChangeSchema` başında yazılı. Son satır 29 Eyl 2026'da eklendi:
 * çevirisi üretilen dillerde de "görünmez" kuralı, çeviriler yazılana kadar
 * dokuz dilin sayfasını boş açmak demekti (`apps/api/src/common/content-locale.ts`).
 *
 * ---------------------------------------------------------------------------
 * Yönetim
 * ---------------------------------------------------------------------------
 * `/backstage/changelog`. Yasal metinden farklı olarak OLUŞTURMA ve SİLME var
 * (gerekçe `admin-changelog.service.ts` başında): yasal belge kümesi ürünün
 * bir yapı taşı, sürüm kaydı ise editoryal içerik — her dağıtımda bir yenisi
 * doğuyor.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Değişiklik türü
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir SATIRIN etiketi — kaydın kendisinin değil.
 *
 * Bir sürümde hem yeni bir özellik hem bir düzeltme olabiliyor; etiket kayıt
 * düzeyinde dursaydı ikisinden birini seçmek gerekirdi ve seçilmeyen sessizce
 * kaybolurdu.
 *
 * Dizideki sıra EKRANDAKİ sıra: önce eklenen, sonra iyileşen, sonra düzelen,
 * en sonda kaldırılan. Alfabetik değil, okunma sırası — ziyaretçinin aradığı
 * şey neredeyse her zaman "ne geldi".
 */
export const CHANGELOG_KINDS = ['ADDED', 'IMPROVED', 'FIXED', 'REMOVED'] as const;

export const changelogKindSchema = z.enum(CHANGELOG_KINDS);
export type ChangelogKind = (typeof CHANGELOG_KINDS)[number];

export const CHANGELOG_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export const changelogStatusSchema = z.enum(CHANGELOG_STATUSES);
export type ChangelogStatus = (typeof CHANGELOG_STATUSES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Değişiklik satırı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tek satır: bir etiket, bir cümle.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden serbest blok listesi DEĞİL — bu dosyanın en önemli kararı
 * ═══════════════════════════════════════════════════════════════════════════
 * Blog ve yasal metin `paragraph | heading | list | table | …` sözlüğüyle
 * yazılıyor. Burada öyle bir sözlük YOK ve bu bir eksiklik değil.
 *
 * Sürüm notunun şekli sabittir: etiketli satırlar. Serbest bir blok editörü
 * verilseydi bir kayıt paragrafla, öteki madde listesiyle, üçüncüsü tabloyla
 * yazılırdı — ve zaman çizelgesi her kaydırmada başka bir sayfa gibi
 * görünürdü. Bir çizelgenin okunabilirliği tam olarak satırların AYNI şekilde
 * durmasından geliyor.
 *
 * İkinci sebep makine tarafında: etiket bir alan olduğu için süzgeç
 * ("yalnızca yenilikler") mümkün. Serbest metinde o süzgeç ancak kelime
 * araması olurdu.
 *
 * Bedeli bilinçli: uzun anlatım istiyorsan burası doğru yer değil, blog
 * doğru yer. Kayıt zaten `summary` ile bir cümle taşıyor; üçüncü bir anlatım
 * katmanı, sürüm notunu duyuru olmaktan çıkarıp makaleye çevirirdi.
 *
 * ---------------------------------------------------------------------------
 * 400 karakter neden
 * ---------------------------------------------------------------------------
 * İki satırlık bir cümle. Daha uzunu, ekranda madde olmaktan çıkıp paragrafa
 * dönüşüyor ve yukarıdaki kararın kendisini deliyor.
 */
export const changelogChangeSchema = z.object({
  kind: changelogKindSchema,
  text: z.string().min(1).max(400),
});

export type ChangelogChange = z.infer<typeof changelogChangeSchema>;

/**
 * Bir kaydın satırlarının tamamı.
 *
 * Tavan 40: kırktan uzun bir sürüm notu okunmuyor, taranıyor — ve tarandığı
 * anda tek tek satırların anlamı kalmıyor. O noktada yapılacak şey kaydı
 * bölmek ya da bir blog yazısına bağlamak.
 *
 * `Json` kolonundan OKURKEN de çalışıyor. Tanınmayan satır DÜŞÜRÜLÜYOR ve
 * düşürüldüğü hem loglanıyor hem panelde gösteriliyor
 * (`changelog.mappers.ts`) — blog/yasal metindeki kararla aynı.
 */
export const changelogItemsSchema = z.array(changelogChangeSchema).max(40);

// ═══════════════════════════════════════════════════════════════════════════
// Slug
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kaydın çıpası — `/changelog#<slug>`.
 *
 * 🔴 Ayrı bir detay sayfası YOK (gerekçe şemada, `ChangelogEntry.slug`
 * üstünde). Slug yine de kalıcı bir adres parçası: "şu sürümde geldi" diye
 * paylaşılan bağlantı doğrudan o kaydı açıyor.
 *
 * Kalıp dar tutuldu: küçük harf, rakam, tire. Türkçe karakter ve büyük harf
 * bir çıpada adres kodlamasına girer, paylaşılan bağlantıyı okunmaz yapar.
 * Panelde alan başlıktan ÖNERİLİYOR ama zorlanmıyor — düzenleyen kişi
 * "2026-09-meta-yayin" gibi kendi düzenini kurabilmeli.
 */
export const changelogSlugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug yalnız küçük harf, rakam ve tire içerebilir.');

// ═══════════════════════════════════════════════════════════════════════════
// Sorgular
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Dizin sorgusu.
 *
 * `kind` süzgeci BURADA YOK ve olmayacak: kayıtların hepsi tek istekte
 * geliyor ve süzgeç istemcide çalışıyor (blog dizini ile aynı karar).
 * Türe basıldığında ağa çıkılsaydı hem kayan işaret (`layoutId`) beklemeye
 * girerdi hem de her basış bir istek olurdu.
 *
 * `limit` yine de var: içerik yüzlerce kayda çıktığında ilk fren burası ve
 * o gün yapılacak iş bellidir — yıl süzgeci + imleç (`cursor`). Bugün
 * eklemek, kullanılmayan bir sayfalayıcıyı iki tarafta da bakımda tutmak
 * olurdu.
 */
export const changelogListQuerySchema = z.object({
  /// Verilmezse İngilizce. Arayüz her zaman gönderiyor (`/[locale]/changelog`).
  locale: localeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(60),
});
export type ChangelogListQuery = z.infer<typeof changelogListQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıtlar — herkese açık
// ═══════════════════════════════════════════════════════════════════════════

export interface ChangelogEntrySummary {
  /// Çıpa: `/changelog#<slug>`.
  slug: string;
  /// `null` = bu duyurunun sürüm numarası yok. Ekran o zaman yalnız tarih
  /// basıyor — uydurulmuş bir numara basmıyor.
  version: string | null;
  /**
   * ISO 8601. `null` OLAMAZ: dizin yalnız yayınlanmış kayıtları döndürüyor ve
   * yayınlanmışın tarihi var (kontrol `changelog.service.ts`).
   */
  releasedAt: string;
  /// Öne çıkan kayıt — listenin BAŞINDA değil, bulunduğu yerde büyük kartla
  /// duruyor. Kronoloji bozulmuyor (gerekçe şemada).
  highlight: boolean;
  coverImage: string | null;
  title: string;
  summary: string;
  items: ChangelogChange[];
  /**
   * Metnin GERÇEK dili.
   *
   * Çevirisi üretilen on bir dilden birinde metni olmayan kayıt İngilizceye
   * (o da yoksa Türkçeye) düşüyor (`common/content-locale.ts`, 29 Eyl 2026);
   * `en`/`tr` istendiğinde düşme YOK, eksik çeviri kaydı gizliyor. Tek
   * sayfada farklı dillerde kayıtlar yan yana durabildiği için dil kayıt
   * başına söyleniyor; arayüz `lang` özniteliğini buradan basıyor.
   */
  locale: Locale;
  /// Düşülmüşse İSTENEN (eksik) dil, düşülmemişse `null` — yasal metinle
  /// aynı sözleşme (`LegalDocDetail.fallbackLocale`).
  fallbackLocale: Locale | null;
}

export interface ChangelogKindCount {
  key: ChangelogKind;
  /**
   * Bu türden en az bir satır taşıyan KAYIT sayısı — satır sayısı değil.
   *
   * Süzgeç kayıtları eliyor, satırları değil: "Düzeltme (3)" yazıp üç kayıt
   * göstermesi gereken bir düğmenin dokuz yazması, sayıyı yalan yapardı.
   *
   * Sayılar süzgeç UYGULANMADAN hesaplanıyor (blog'un konu sayaçlarıyla aynı
   * gerekçe): süzülmüş listeden hesaplansaydı bir tür seçildiğinde
   * ötekilerin hepsi sıfır görünürdü ve süzgeç bir sonuç değil, navigasyon.
   */
  count: number;
}

export interface ChangelogListResponse {
  entries: ChangelogEntrySummary[];
  kinds: ChangelogKindCount[];
  total: number;
  /// İSTENEN dil. Kayıtların her biri kendi metninin dilini ayrıca taşıyor
  /// (`ChangelogEntrySummary.locale`): çevirisi üretilen dillerde çevirisi
  /// olmayan kayıt listeden düşmüyor, İngilizce ya da Türkçe metniyle
  /// duruyor. `en` ve `tr` sayfasında o dilde metni olmayan kayıt hiç yok.
  locale: Locale;
  /**
   * Sayfanın arama motoru etiketleri — EN YENİ kaydın alanlarından.
   *
   * Kayıtların kendi sayfası olmadığı için SEO tek bir adresin sorunu ve o
   * adres her yeni kayıtla tazeleniyor. En yeni kaydın açıklaması, sabit bir
   * "ürün güncellemeleri" cümlesinden çok daha bilgilendirici.
   *
   * `null` = en yeni kayıtta SEO alanı doldurulmamış; arayüz kendi
   * `messages` metnine düşüyor.
   */
  seoTitle: string | null;
  seoDescription: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hatalar
// ═══════════════════════════════════════════════════════════════════════════

export const ChangelogErrorCode = {
  /// Panelde: böyle bir kayıt yok (silinmiş ya da kimlik yanlış).
  ENTRY_NOT_FOUND: 'CHANGELOG_ENTRY_NOT_FOUND',
  /// Yayına alınmak istenen kaydın tarihi ya da hiçbir dilde metni yok.
  NOT_PUBLISHABLE: 'CHANGELOG_ENTRY_NOT_PUBLISHABLE',
  /// Slug başka bir kayıtta kullanılıyor. Çıpa tekil olmak zorunda: iki kayıt
  /// aynı çıpayı taşısaydı paylaşılan bağlantı hangisini açacağını bilemezdi.
  SLUG_TAKEN: 'CHANGELOG_SLUG_TAKEN',
} as const;
export type ChangelogErrorCode = (typeof ChangelogErrorCode)[keyof typeof ChangelogErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// Yönetim paneli — /backstage/changelog
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Panelin liste satırı.
 *
 * Satırların METNİ taşınmıyor, yalnız başlığı ve hangi dillerin dolu olduğu:
 * yüz kaydın iki dildeki tam satır listesi tek bir liste isteğinde yüzlerce
 * kilobayt olurdu. Metin, kayıt açıldığında geliyor (yasal metin ekranıyla
 * aynı karar).
 */
export interface AdminChangelogRow {
  id: string;
  slug: string;
  status: ChangelogStatus;
  version: string | null;
  releasedAt: string | null;
  highlight: boolean;
  updatedAt: string;
  /// Hangi dillerde metin var. Sıra `LOCALES`in kendi sırası (`locales.ts`,
  /// on üç dil) — panelde sekmelerin sırası da bu.
  locales: { locale: Locale; title: string; itemCount: number }[];
}

export interface AdminChangelogTranslation {
  locale: Locale;
  title: string;
  summary: string;
  items: ChangelogChange[];
  seoTitle: string | null;
  seoDescription: string | null;
  updatedAt: string;
  /**
   * Okurken düşürülen satır sayısı.
   *
   * Editör bunu göstermek zorunda: kolon şema baskısı olmayan `Json` ve elle
   * düzenlenebiliyor. Sessizce düşen bir satır, "kaydettim ama bir madde
   * kayboldu" demek.
   */
  droppedItems: number;
}

export interface AdminChangelogDetail {
  id: string;
  slug: string;
  status: ChangelogStatus;
  version: string | null;
  releasedAt: string | null;
  highlight: boolean;
  coverImage: string | null;
  createdAt: string;
  updatedAt: string;
  translations: AdminChangelogTranslation[];
}

/**
 * Yeni kayıt — ÜÇ alan.
 *
 * Neden burada slug yok: kayıt doğduğu anda bir başlığı var ve slug o
 * başlıktan türetiliyor (`anchorFor`, blog/yasal metinle ORTAK). Formda
 * dördüncü bir alan olsaydı her yeni kayıtta doldurulması gereken ama
 * neredeyse hep aynı şeyi yazan bir alan olurdu. Türetilen slug sonradan
 * üstveri formundan değiştirilebiliyor.
 *
 * Neden `summary` zorunlu: özetsiz bir kayıt, listede yalnız başlıkla duruyor
 * ve "bu beni ilgilendiriyor mu" sorusunu cevaplamıyor. Zorunlu olması,
 * kaydı yazan kişiyi doğduğu anda o cümleyi kurmaya zorluyor —
 * sonradan doldurulacak bir alan, doldurulmuyor.
 */
export const adminChangelogCreateSchema = z.object({
  /// Hangi dilde yazılıyor. Panelin o anki sekmesi geliyor.
  locale: localeSchema,
  title: z.string().min(1).max(200),
  summary: z.string().min(1).max(600),
});
export type AdminChangelogCreateInput = z.infer<typeof adminChangelogCreateSchema>;

/**
 * Kaydın dil ÜSTÜ alanları.
 *
 * `status` burada: yayına almak iki dili birden ilgilendiriyor. Çevirinin
 * kendi yayın durumu YOK — "Türkçesi yayında, İngilizcesi taslak" bir sürüm
 * duyurusu için anlamsız; ikisi aynı duyurunun iki dili.
 */
export const adminChangelogMetaSchema = z.object({
  slug: changelogSlugSchema,
  status: changelogStatusSchema,
  /**
   * Boş bırakılabilir — `null` gidiyor, boş dize değil.
   *
   * İkisi sözleşmede AYRI: `null` "bu duyurunun sürüm numarası yok", `''` ise
   * geçerli ama görünmez bir rozet olurdu.
   */
  version: z.string().max(40).nullable().default(null),
  /// ISO 8601. `null` yalnız taslakta geçerli — kontrol serviste, çünkü
  /// `status` ile ilişkili bir kural ve zod tek alana bakıyor.
  releasedAt: z.iso.datetime().nullable(),
  highlight: z.boolean().default(false),
  /**
   * Kapak görselinin adresi.
   *
   * `z.url()` DEĞİL bilerek: panelde `/shots/changelog/x.png` gibi site-içi
   * bir yol da yazılabiliyor ve mutlak URL zorlamak o yolu reddederdi.
   * Doğrulama uzunlukla sınırlı; adresin gerçekten bir görsel olup olmadığını
   * tarayıcı söyleyecek.
   */
  coverImage: z.string().max(500).nullable().default(null),
});
export type AdminChangelogMetaInput = z.infer<typeof adminChangelogMetaSchema>;

/**
 * Bir dildeki metnin TAMAMI.
 *
 * Kısmi güncelleme (PATCH) yok: satırların sırası bilginin kendisi ve bir
 * satırı tek başına yamalamak, sırayı iki istemci arasında yarışa sokardı.
 */
export const adminChangelogTranslationSchema = z.object({
  title: z.string().min(1).max(200),
  summary: z.string().min(1).max(600),
  /**
   * En az bir satır.
   *
   * Satırsız bir sürüm kaydı yalnızca başlık ve özetten ibaret olurdu — o da
   * bir duyuru, bir değişiklik kaydı değil. Ekranın vaat ettiği şey "ne
   * değişti" ve cevabı boş bırakılamaz.
   */
  items: changelogItemsSchema.min(1),
  seoTitle: z.string().max(200).nullable().default(null),
  seoDescription: z.string().max(400).nullable().default(null),
});
export type AdminChangelogTranslationInput = z.infer<typeof adminChangelogTranslationSchema>;
